import i18n from '@i18n';

/**
 * 画布移动工具函数
 * 提供一种直接的方式来控制画布移动，不依赖于Leafer框架的内部DOM结构
 */

// 存储全局拖拽状态
let isDragging = false;
let startPos = { x: 0, y: 0 };
let startTransform = { x: 0, y: 0 };
let dragHandlers = null;

/**
 * 获取最合适的容器元素
 * @param {Object} app Leafer应用实例
 * @returns {HTMLElement} 容器元素
 */
function getContainer(app) {
  // 尝试获取各种可能的容器元素
  if (app.view && app.view.canvas) {
    return app.view.canvas;
  }
  
  if (app.view) {
    return app.view;
  }
  
  // 尝试通过ID查找
  const container = document.getElementById('editor-canvas-container') || 
                    document.getElementById('editor-container');
  
  if (container) {
    return container;
  }
  
  // 使用Leafer的树容器元素
  if (app.tree && app.tree.dom && app.tree.dom.element) {
    return app.tree.dom.element;
  }
  
  // 最后的尝试 - 查找任何canvas元素
  const canvases = document.querySelectorAll('canvas');
  if (canvases.length > 0) {
    return canvases[0];
  }
  
  console.error(i18n.t('editor:utils.canvasMove.cannotFindContainer'));
  return null;
}

/**
 * 获取画布当前的变换信息
 * @param {Object} app Leafer应用实例
 * @returns {Object} 包含x和y变换值的对象
 */
function getCurrentTransform(app) {
  // 默认值
  const defaultTransform = { x: 0, y: 0 };
  
  try {
    // 尝试从应用树中获取
    if (app.tree) {
      return { 
        x: app.tree.x || 0, 
        y: app.tree.y || 0 
      };
    }
    
    // 尝试从DOM样式中获取
    const container = getContainer(app);
    if (container && container.style && container.style.transform) {
      const transformStr = container.style.transform;
      const match = transformStr.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
      if (match && match.length >= 3) {
        return {
          x: parseFloat(match[1]) || 0,
          y: parseFloat(match[2]) || 0
        };
      }
    }
    
    return defaultTransform;
  } catch (e) {
    console.error(i18n.t('editor:utils.canvasMove.getTransformFailed'), e);
    return defaultTransform;
  }
}

/**
 * 应用变换到画布
 * @param {Object} app Leafer应用实例
 * @param {number} x X轴位移
 * @param {number} y Y轴位移
 */
function applyTransform(app, x, y) {
  try {
    // 先尝试通过应用实例更新位置
    if (app.tree) {
      app.tree.x = x;
      app.tree.y = y;
      console.log(i18n.t('editor:utils.canvasMove.applyTransformViaTree'), x, y);
      
      // 尝试更新内部DOM元素
      if (app.tree.dom && app.tree.dom.element) {
        app.tree.dom.element.style.transform = `translate(${x}px, ${y}px)`;
      }
      
      return; // 成功应用Leafer变换，直接返回
    }
    
    // 如果Leafer方法失败，尝试通过DOM样式直接设置变换
    const container = getContainer(app);
    if (container) {
      container.style.transform = `translate(${x}px, ${y}px)`;
      // 添加自定义类以标识元素
      container.classList.add('leafer-dragging');
      console.log(i18n.t('editor:utils.canvasMove.applyTransformViaDOM'), x, y);
      
      // 延迟移除类，保持平滑过渡
      setTimeout(() => {
        container.classList.remove('leafer-dragging');
      }, 100);
    }
  } catch (e) {
    console.error(i18n.t('editor:utils.canvasMove.applyTransformFailed'), e);
  }
}

/**
 * 设置鼠标样式
 * @param {Object} app Leafer应用实例
 * @param {string} cursor 光标样式
 */
function setCursor(app, cursor) {
  try {
    // 尝试通过应用实例设置
    if (app.view) {
      app.view.style.cursor = cursor;
    }
    
    // 尝试通过容器直接设置
    const container = getContainer(app);
    if (container) {
      container.style.cursor = cursor;
    }
    
    // 如果是grabbing光标，添加body类
    if (cursor === 'grabbing') {
      document.body.classList.add('canvas-dragging');
    } else if (document.body.classList.contains('canvas-dragging')) {
      document.body.classList.remove('canvas-dragging');
    }
  } catch (e) {
    console.error(i18n.t('editor:utils.canvasMove.setCursorFailed'), e);
  }
}

/**
 * 启用画布移动模式
 * @param {Object} app Leafer应用实例
 */
export function enableCanvasMove(app) {
  if (!app) {
    console.error(i18n.t('editor:utils.canvasMove.enableFailedAppNull'));
    return;
  }
  
  console.log(i18n.t('editor:utils.canvasMove.enableMoveMode'));
  
  try {
    // 获取容器元素
    const container = getContainer(app);
    if (!container) {
      console.error(i18n.t('editor:utils.canvasMove.cannotFindContainerEnable'));
      return;
    }
    
    // 添加拖拽类
    container.classList.add('canvas-move-mode');
    
    // 设置鼠标样式
    setCursor(app, 'grab');
    
    // 配置拖拽和点击相关设置
    if (app.editor) {
      app.editor.hittable = false;
      
      // 取消当前选择
      if (app.editor.list && app.editor.list.length) {
        app.editor.clearSelect();
      }
    }
    
    if (app.config) {
      if (app.config.pick) app.config.pick.hittable = false;
      if (app.config.move) app.config.move.drag = true;
    }
    
    if (app.tree) {
      app.tree.hittable = false;
      app.tree.draggable = true;
    }
    
    // 清除之前的事件处理程序（如果存在）
    disableDragHandlers();
    
    // 定义鼠标按下事件处理函数
    const handleMouseDown = (e) => {
      // 防止文本选择和默认行为
      e.preventDefault();
      
      // 初始化拖拽状态
      isDragging = true;
      startPos = { x: e.clientX, y: e.clientY };
      startTransform = getCurrentTransform(app);
      
      // 更改光标样式
      setCursor(app, 'grabbing');
      
      console.log(i18n.t('editor:utils.canvasMove.startDrag'), startPos, startTransform);
    };
    
    // 定义鼠标移动事件处理函数
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      // 计算偏移量
      const dx = e.clientX - startPos.x;
      const dy = e.clientY - startPos.y;
      
      // 应用新位置
      const newX = startTransform.x + dx;
      const newY = startTransform.y + dy;
      applyTransform(app, newX, newY);
    };
    
    // 定义鼠标释放事件处理函数
    const handleMouseUp = () => {
      if (!isDragging) return;
      
      // 重置拖拽状态
      isDragging = false;
      setCursor(app, 'grab');
      
      console.log(i18n.t('editor:utils.canvasMove.endDrag'));
    };
    
    // 定义触摸事件处理函数
    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      
      // 防止默认行为（如滚动）
      e.preventDefault();
      
      // 初始化拖拽状态
      isDragging = true;
      startPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      startTransform = getCurrentTransform(app);
      
      // 更改光标样式（虽然在触摸设备上不显示）
      setCursor(app, 'grabbing');
      
      console.log(i18n.t('editor:utils.canvasMove.startTouchDrag'), startPos, startTransform);
    };
    
    const handleTouchMove = (e) => {
      if (!isDragging || e.touches.length !== 1) return;
      
      // 阻止页面滚动
      e.preventDefault();
      
      // 计算偏移量
      const dx = e.touches[0].clientX - startPos.x;
      const dy = e.touches[0].clientY - startPos.y;
      
      // 应用新位置
      const newX = startTransform.x + dx;
      const newY = startTransform.y + dy;
      applyTransform(app, newX, newY);
    };
    
    const handleTouchEnd = () => {
      if (!isDragging) return;
      
      // 重置拖拽状态
      isDragging = false;
      setCursor(app, 'grab');
      
      console.log(i18n.t('editor:utils.canvasMove.endTouchDrag'));
    };
    
    // 添加事件监听
    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    container.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    // 保存引用，以便稍后可以移除
    dragHandlers = {
      container,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd
    };
    
    console.log(i18n.t('editor:utils.canvasMove.moveModeEnabled'));
  } catch (error) {
    console.error(i18n.t('editor:utils.canvasMove.enableError'), error);
  }
}

/**
 * 移除拖拽事件处理程序
 */
function disableDragHandlers() {
  if (!dragHandlers) return;
  
  const {
    container,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  } = dragHandlers;
  
  // 移除事件监听
  if (container) {
    container.removeEventListener('mousedown', handleMouseDown);
    container.removeEventListener('touchstart', handleTouchStart);
    container.classList.remove('canvas-move-mode');
  }
  
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
  document.removeEventListener('touchmove', handleTouchMove);
  document.removeEventListener('touchend', handleTouchEnd);
  
  // 确保body类被移除
  document.body.classList.remove('canvas-dragging');
  
  // 清空引用
  dragHandlers = null;
  isDragging = false;
  
  console.log(i18n.t('editor:utils.canvasMove.dragHandlersRemoved'));
}

/**
 * 禁用画布移动模式
 * @param {Object} app Leafer应用实例
 */
export function disableCanvasMove(app) {
  if (!app) {
    console.error(i18n.t('editor:utils.canvasMove.disableFailedAppNull'));
    return;
  }
  
  console.log(i18n.t('editor:utils.canvasMove.disableMoveMode'));
  
  try {
    // 设置鼠标样式
    setCursor(app, 'default');
    
    // 移除拖拽事件处理程序
    disableDragHandlers();
    
    // 配置拖拽和点击相关设置
    if (app.editor) {
      app.editor.hittable = true;
    }
    
    if (app.config) {
      if (app.config.pick) app.config.pick.hittable = true;
      if (app.config.move) app.config.move.drag = false;
    }
    
    if (app.tree) {
      app.tree.hittable = true;
      app.tree.draggable = false;
    }
    
    console.log(i18n.t('editor:utils.canvasMove.moveModeDisabled'));
  } catch (error) {
    console.error(i18n.t('editor:utils.canvasMove.disableError'), error);
  }
}

/**
 * 切换画布移动模式
 * @param {Object} app Leafer应用实例
 * @param {boolean} isEnabled 是否启用移动模式
 */
export function toggleCanvasMove(app, isEnabled) {
  console.log(`${i18n.t('editor:utils.canvasMove.toggleMoveMode')} ${isEnabled ? i18n.t('editor:utils.canvasMove.enable') : i18n.t('editor:utils.canvasMove.disable')}`);
  
  try {
    if (isEnabled) {
      enableCanvasMove(app);
    } else {
      disableCanvasMove(app);
    }
    
    return isEnabled;
  } catch (error) {
    console.error(i18n.t('editor:utils.canvasMove.toggleError'), error);
    return false;
  }
}
